import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { learningService } from '../../services/learningService';
import { dictionaryService } from '../../services/dictionaryService';
import { cefrKnowledgeService, CEFRLevel } from '../../services/cefrKnowledgeService';
import { VocabularyWithProgress } from '../../types/learning';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import { useLearningSession } from '../../hooks/useLearningSession';
import { useAuth } from '../../contexts/AuthContext';
import { checkPronunciation, PronunciationCheckResult } from '../../services/vocabularyPronunciationService';
import { convertPhonetic, getUserPhoneticSystem } from '../../utils/phoneticConverter';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import XpToastContainer, { useXpToast } from '../../components/learning/XpToast';
import LetterPicker from '../../components/learning/LetterPicker';
import InputModeToggle from '../../components/learning/InputModeToggle';
import { useDictationInputMode } from '../../hooks/useDictationInputMode';
import { usePhonicsErrorCollector } from '../../hooks/usePhonicsErrorCollector';
import { usePhonicsGuidance } from '../../hooks/usePhonicsGuidance';
import PhonicsSessionSummary from '../../components/learning/PhonicsSessionSummary';
import './VocabularyPage.css';
import './SentenceNav.css';

// Two stages for vocabulary learning
type LearningStage = 'speaking' | 'dictation';

interface WordProgress {
  speaking: boolean;
  dictation: boolean;
}

const VocabularyPage: React.FC = () => {
  const { t } = useTranslation('learning');
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toasts: xpToasts, showXp } = useXpToast();
  const [vocabulary, setVocabulary] = useState<VocabularyWithProgress[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSessionComplete, setIsSessionComplete] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Total vocabulary reviewed count (from backend)
  const [totalReviewedCount, setTotalReviewedCount] = useState(0);

  // Mastery change animation state
  const [masteryDelta, setMasteryDelta] = useState<number | null>(null);

  // CEFR level mapping for vocabulary words
  const [cefrLevelMap, setCefrLevelMap] = useState<Record<string, CEFRLevel>>({});

  // Two-stage learning state
  const [currentStage, setCurrentStage] = useState<LearningStage>('speaking');
  const [, setWordProgressMap] = useState<Record<string, WordProgress>>({});

  // Dictation state
  const [, setDictationInput] = useState('');
  const [dictationResult, setDictationResult] = useState<'correct' | 'incorrect' | null>(null);
  const [dictationHadErrors, setDictationHadErrors] = useState(false); // Track if user made errors

  // Auto-hint state (shows first letter after 10 seconds of no input)
  const [showAutoHint, setShowAutoHint] = useState(false);
  const autoHintTimerRef = useRef<NodeJS.Timeout | null>(null);
  const AUTO_HINT_DELAY_MS = 10000; // 10 seconds

  // Letter-by-letter input state (for when answer is incorrect)
  const [letterBoxMode, setLetterBoxMode] = useState(false);
  const [lockedLetters, setLockedLetters] = useState<boolean[]>([]);
  const [letterInputs, setLetterInputs] = useState<string[]>([]);
  const letterInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { isLetterPicker, toggle: toggleInputMode } = useDictationInputMode();
  const { addErrors: addPhonicsErrors } = usePhonicsErrorCollector(courseId, 'vocabulary');
  const { handleLetterError, getSessionErrors } = usePhonicsGuidance();
  const [letterPickerResetKey, setLetterPickerResetKey] = useState(0);

  // Pronunciation check state
  const { isRecording, audioBlob, audioUrl: recordedAudioUrl, startRecording, stopRecording, clearRecording, error: recordingError } = useAudioRecorder();
  const [isCheckingPronunciation, setIsCheckingPronunciation] = useState(false);

  // Learning session tracking (time spent in this mode)
  const currentWord = vocabulary[currentIndex];
  const { updateContext: updateSessionContext } = useLearningSession({
    courseId: courseId || '',
    mode: 'vocabulary',
    enabled: !!courseId && !!user,
    contextData: { vocabularyWordId: currentWord?.id },
  });

  // Update session context when word changes
  useEffect(() => {
    if (currentWord?.id) {
      updateSessionContext({ vocabularyWordId: currentWord.id });
    }
  }, [currentWord?.id, updateSessionContext]);
  const [pronunciationResult, setPronunciationResult] = useState<PronunciationCheckResult | null>(null);
  const [pronunciationError, setPronunciationError] = useState<string | null>(null);
  const [pronunciationAttempts, setPronunciationAttempts] = useState(0);

  // Audio duration thresholds
  const MIN_AUDIO_DURATION = 0.3;
  const MAX_AUDIO_DURATION = 10;

  // Debounce ref for spacebar audio playback
  const lastAudioPlayTimeRef = useRef<number>(0);
  const AUDIO_DEBOUNCE_MS = 500;

  // Ref to store latest handlePronunciationCheck (to avoid stale closures in useEffect)
  const handlePronunciationCheckRef = useRef<(blob: Blob) => Promise<void>>();

  // Normalize whitespace in a string (collapse multiple spaces to single space, trim)
  const normalizeWhitespace = useCallback((str: string): string => {
    return str.replace(/\s+/g, ' ').trim();
  }, []);

  // Get user's phonetic system preference
  const phoneticSystem = useMemo(() => getUserPhoneticSystem(), []);

  const loadVocabulary = useCallback(async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    try {
      // Load vocabulary and progress summary in parallel
      const [data, progressSummary] = await Promise.all([
        learningService.getVocabulary(courseId),
        learningService.getProgressSummary(courseId).catch(() => null),
      ]);

      setVocabulary(data);

      // Set total reviewed count from backend
      const reviewedCount = progressSummary?.vocabulary?.completedCount || 0;
      setTotalReviewedCount(reviewedCount);

      // Resume from saved position if available
      const savedIndex = progressSummary?.vocabulary?.currentIndex;
      if (savedIndex !== undefined && savedIndex < data.length) {
        // Start from the next word after the last completed one
        const resumeIndex = Math.min(savedIndex + 1, data.length - 1);
        setCurrentIndex(resumeIndex);
      } else {
        setCurrentIndex(0);
      }

      clearAllStates();
    } catch (err) {
      console.error('Failed to load vocabulary:', err);
      setError(t('vocabularyPractice.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearAllStates only uses stable state setters
  }, [courseId, t]);

  // Load vocabulary
  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  // Load CEFR levels for course vocabulary
  useEffect(() => {
    let isMounted = true;
    const loadCefrLevels = async () => {
      if (!courseId) return;
      try {
        const stats = await cefrKnowledgeService.getCourseKnowledgePointStats(courseId);
        if (isMounted) {
          const levelMap: Record<string, CEFRLevel> = {};
          for (const kp of stats.knowledgePoints) {
            if (kp.category === 'vocabulary') {
              levelMap[kp.term.toLowerCase()] = kp.cefrLevel;
            }
          }
          setCefrLevelMap(levelMap);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load CEFR levels:', error);
        }
      }
    };
    loadCefrLevels();
    return () => { isMounted = false; };
  }, [courseId]);

  // Auto-check pronunciation when recording stops
  useEffect(() => {
    if (!audioBlob || !recordedAudioUrl || isRecording || vocabulary.length === 0) return;

    const audio = new Audio(recordedAudioUrl);

    const handleLoadedMetadata = () => {
      const duration = audio.duration;

      // Skip duration check if duration is Infinity or NaN (common with WebM format)
      if (!isFinite(duration) || isNaN(duration)) {
        handlePronunciationCheckRef.current?.(audioBlob);
        return;
      }

      if (duration < MIN_AUDIO_DURATION) {
        setPronunciationError(t('vocabularyPractice.recording.tooShort'));
        clearRecording();
      } else if (duration > MAX_AUDIO_DURATION) {
        setPronunciationError(t('vocabularyPractice.recording.tooLong'));
        clearRecording();
      } else {
        // Use ref to get latest function (avoids stale closure)
        handlePronunciationCheckRef.current?.(audioBlob);
      }
    };

    const handleError = () => {
      // Use ref to get latest function (avoids stale closure)
      handlePronunciationCheckRef.current?.(audioBlob);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, [audioBlob, recordedAudioUrl, isRecording, vocabulary.length, t, clearRecording]);

  // Focus first empty input when entering letter box mode
  useEffect(() => {
    if (letterBoxMode && lockedLetters.length > 0) {
      const firstEmptyIndex = lockedLetters.findIndex(l => !l);
      if (firstEmptyIndex >= 0) {
        setTimeout(() => {
          letterInputRefs.current[firstEmptyIndex]?.focus();
        }, 50);
      }
    }
  }, [letterBoxMode, lockedLetters]);

  // Handle pronunciation check
  const handlePronunciationCheck = async (blob: Blob) => {
    if (!blob || vocabulary.length === 0) return;

    const currentItem = vocabulary[currentIndex];
    setIsCheckingPronunciation(true);
    setPronunciationError(null);
    setPronunciationAttempts(prev => prev + 1);

    try {
      const response = await checkPronunciation(blob, currentItem.word);
      if (response.success && response.data) {
        setPronunciationResult(response.data);
      } else {
        setPronunciationError(response.error || t('vocabularyPractice.pronunciation.checkFailed'));
      }
    } catch (err) {
      console.error('Pronunciation check error:', err);
      setPronunciationError(t('vocabularyPractice.pronunciation.checkFailedRetry'));
    } finally {
      setIsCheckingPronunciation(false);
      clearRecording();
    }
  };

  // Keep ref updated with latest handlePronunciationCheck
  handlePronunciationCheckRef.current = handlePronunciationCheck;

  // Clear pronunciation result when retrying
  const clearPronunciationState = () => {
    setPronunciationResult(null);
    setPronunciationError(null);
    clearRecording();
  };

  const resetPronunciationAttempts = () => {
    setPronunciationAttempts(0);
  };

  // Clear dictation state
  const clearDictationState = () => {
    setDictationInput('');
    setDictationResult(null);
    setDictationHadErrors(false);
    setLetterBoxMode(false);
    setLockedLetters([]);
    setLetterInputs([]);
    setLetterPickerResetKey(k => k + 1);
    // Clear auto-hint state
    setShowAutoHint(false);
    if (autoHintTimerRef.current) {
      clearTimeout(autoHintTimerRef.current);
      autoHintTimerRef.current = null;
    }
  };

  // Reset auto-hint timer (call when user interacts with input)
  const resetAutoHintTimer = useCallback(() => {
    setShowAutoHint(false);
    if (autoHintTimerRef.current) {
      clearTimeout(autoHintTimerRef.current);
    }
    autoHintTimerRef.current = setTimeout(() => {
      setShowAutoHint(true);
      setDictationHadErrors(true); // Using auto-hint counts as not first try
    }, AUTO_HINT_DELAY_MS);
  }, []); // AUTO_HINT_DELAY_MS is a constant, no deps needed

  // Start auto-hint timer when entering dictation stage
  useEffect(() => {
    let isMounted = true;

    if (currentStage === 'dictation' && letterBoxMode && dictationResult !== 'correct') {
      setShowAutoHint(false);
      if (autoHintTimerRef.current) {
        clearTimeout(autoHintTimerRef.current);
      }
      autoHintTimerRef.current = setTimeout(() => {
        if (isMounted) {
          setShowAutoHint(true);
          setDictationHadErrors(true);
        }
      }, AUTO_HINT_DELAY_MS);
    }

    return () => {
      isMounted = false;
      if (autoHintTimerRef.current) {
        clearTimeout(autoHintTimerRef.current);
      }
    };
  }, [currentStage, letterBoxMode, dictationResult]);

  // Clear all states when moving to next word
  const clearAllStates = () => {
    clearPronunciationState();
    clearDictationState();
    resetPronunciationAttempts();
    setCurrentStage('speaking');
  };

  const handleSkipWord = useCallback(() => {
    if (currentIndex < vocabulary.length - 1) {
      setCurrentIndex(currentIndex + 1);
      clearAllStates();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentIndex, vocabulary.length]);

  const canSkipWord = currentIndex < vocabulary.length - 1;

  // Note: wordProgressMap is used to track session-local progress for speaking/dictation stages
  // The actual reviewed count is tracked by totalReviewedCount from the backend

  // Handle speaking stage completion - move to dictation
  const handleSpeakingComplete = useCallback(() => {
    const currentItem = vocabulary[currentIndex];
    if (!currentItem) return;

    // Show mastery animation: +2 if first attempt, +1 if more
    const delta = pronunciationAttempts <= 1 ? 2 : 1;
    setMasteryDelta(delta);
    setTimeout(() => setMasteryDelta(null), 1000);

    // Mark speaking stage as done
    setWordProgressMap(prev => ({
      ...prev,
      [currentItem.id]: {
        ...prev[currentItem.id],
        speaking: true,
        dictation: prev[currentItem.id]?.dictation || false,
      },
    }));

    // Initialize letter boxes for dictation stage
    // For phrases, spaces are treated as visual separators (locked and pre-filled)
    // Normalize whitespace to handle multiple spaces consistently
    const word = normalizeWhitespace(currentItem.word);
    const locked: boolean[] = [];
    const inputs: string[] = [];

    for (let i = 0; i < word.length; i++) {
      if (word[i] === ' ') {
        // Space is a separator - lock it and pre-fill
        locked.push(true);
        inputs.push(' ');
      } else {
        locked.push(false);
        inputs.push('');
      }
    }

    setLetterBoxMode(true);
    setLockedLetters(locked);
    setLetterInputs(inputs);

    // Move to dictation stage
    setCurrentStage('dictation');
    clearPronunciationState();
    // clearPronunciationState is a stable plain function using only setState calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, vocabulary, pronunciationAttempts, normalizeWhitespace]);

  // Handle letter box input change
  const handleLetterInputChange = useCallback((index: number, value: string) => {
    if (lockedLetters[index]) return;

    // Reset auto-hint timer on any input
    resetAutoHintTimer();

    const newInputs = [...letterInputs];
    const char = value.slice(-1).toLowerCase();
    newInputs[index] = char;
    setLetterInputs(newInputs);

    if (char && index < letterInputs.length - 1) {
      for (let i = index + 1; i < letterInputs.length; i++) {
        if (!lockedLetters[i]) {
          letterInputRefs.current[i]?.focus();
          break;
        }
      }
    }
  }, [lockedLetters, letterInputs, resetAutoHintTimer]);

  // Handle letter box submit
  const handleLetterBoxSubmit = useCallback(() => {
    const currentItem = vocabulary[currentIndex];
    if (!currentItem) return;

    // Normalize whitespace for comparison
    const expected = normalizeWhitespace(currentItem.word).toLowerCase();
    const userWord = letterInputs.join('').toLowerCase();

    if (userWord === expected) {
      setDictationResult('correct');
      setLetterBoxMode(false);
      setWordProgressMap(prev => ({
        ...prev,
        [currentItem.id]: {
          ...prev[currentItem.id],
          speaking: prev[currentItem.id]?.speaking || false,
          dictation: true,
        },
      }));
    } else {
      // User made errors - mark for reduced score
      setDictationHadErrors(true);

      const locked: boolean[] = [];
      const inputs: string[] = [];

      for (let i = 0; i < expected.length; i++) {
        const userChar = letterInputs[i]?.toLowerCase() || '';
        const expectedChar = expected[i].toLowerCase();

        if (userChar === expectedChar) {
          locked.push(true);
          inputs.push(expectedChar);
        } else {
          locked.push(false);
          inputs.push('');
        }
      }

      setLockedLetters(locked);
      setLetterInputs(inputs);

      setTimeout(() => {
        const firstEmptyIndex = locked.findIndex(l => !l);
        if (firstEmptyIndex >= 0) {
          letterInputRefs.current[firstEmptyIndex]?.focus();
        }
      }, 50);
    }
  }, [currentIndex, vocabulary, letterInputs, normalizeWhitespace]);

  // Handle backspace and spacebar in letter box mode
  const handleLetterKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !letterInputs[index]) {
      for (let i = index - 1; i >= 0; i--) {
        if (!lockedLetters[i]) {
          letterInputRefs.current[i]?.focus();
          break;
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleLetterBoxSubmit();
    } else if (e.key === ' ') {
      // Spacebar toggles audio playback instead of typing (with debounce)
      e.preventDefault();
      const now = Date.now();
      if (now - lastAudioPlayTimeRef.current < AUDIO_DEBOUNCE_MS) {
        return; // Debounce: ignore rapid spacebar presses
      }
      lastAudioPlayTimeRef.current = now;

      const currentItem = vocabulary[currentIndex];
      if (currentItem) {
        playPronunciation(currentItem.word);
      }
    }
  }, [letterInputs, lockedLetters, handleLetterBoxSubmit, currentIndex, vocabulary, AUDIO_DEBOUNCE_MS]);

  // Handle dictation complete - move to next word
  const handleDictationComplete = useCallback(async () => {
    if (isSessionComplete || vocabulary.length === 0) return;

    const currentItem = vocabulary[currentIndex];

    // Calculate and show dictation stage mastery animation
    // +2 for first try correct, +1 if used auto-hint or made errors
    const dictationDelta = dictationHadErrors ? 1 : 2;
    setMasteryDelta(dictationDelta);
    setTimeout(() => setMasteryDelta(null), 1000);

    // Call API to update mastery (backend calculates based on success)
    // Pass courseId to track first review progress and wordIndex for resume
    const isFirstReview = !currentItem.userProgress || currentItem.userProgress.reviewCount === 0;
    try {
      const reviewResult = await learningService.reviewVocabulary(currentItem.id, {
        success: true,
        timeSpentSeconds: 30,
        courseId,
        wordIndex: currentIndex,
      });
      // Show XP toast
      if (reviewResult.xpEarned > 0) {
        showXp(reviewResult.xpEarned, reviewResult.leveledUp, reviewResult.newLevel);
      }
      // Increment local count if this was a first review
      if (isFirstReview) {
        setTotalReviewedCount(prev => prev + 1);
      }
    } catch (err: any) {
      console.error('Failed to update mastery:', err);
    }

    // Move to next word or complete session
    if (currentIndex < vocabulary.length - 1) {
      setCurrentIndex(currentIndex + 1);
      clearAllStates();
    } else {
      setIsSessionComplete(true);
    }
    // clearAllStates is a stable plain function using only setState calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, vocabulary, isSessionComplete, dictationHadErrors, courseId]);

  // LetterPicker bridge: word is correct → auto-complete dictation
  const handleVocabLetterPickerComplete = useCallback(() => {
    setDictationResult('correct');
    setTimeout(() => handleDictationComplete(), 300);
  }, [handleDictationComplete]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading || error || isSessionComplete) return;

      if (e.key === 'Enter') {
        e.preventDefault();

        if (currentStage === 'speaking') {
          if (pronunciationResult?.isCorrect) {
            handleSpeakingComplete();
          } else if (isRecording) {
            stopRecording();
          } else if (pronunciationResult && !pronunciationResult.isCorrect) {
            clearPronunciationState();
            setTimeout(() => startRecording(), 100);
          } else if (!isCheckingPronunciation) {
            setPronunciationResult(null);
            setPronunciationError(null);
            startRecording();
          }
        }

        if (currentStage === 'dictation') {
          if (dictationResult === 'correct') {
            handleDictationComplete();
          }
        }
        return;
      }

      if (e.key === 'Escape') {
        navigate(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // clearPronunciationState is a stable plain function using only setState calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, isSessionComplete, navigate, currentStage, dictationResult, handleDictationComplete, pronunciationResult, isRecording, isCheckingPronunciation, handleSpeakingComplete, startRecording, stopRecording]);

  // Play pronunciation
  // Reuse Audio element to avoid mobile autoplay restrictions (iOS Safari)
  const playPronunciation = async (word: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setPlayingAudio(true);

    try {
      const data = await dictionaryService.lookup(word, { generateAudio: true });
      if (data.audio && data.audio.length > 0) {
        const audioUrl = data.audio[0].url;
        if (audioUrl) {
          const url = resolveMediaUrl(audioUrl);
          if (!audioRef.current) {
            audioRef.current = new Audio();
          }
          audioRef.current.src = url;
          audioRef.current.onended = () => setPlayingAudio(false);
          audioRef.current.onerror = () => setPlayingAudio(false);
          audioRef.current.play().catch(() => setPlayingAudio(false));
        } else {
          setPlayingAudio(false);
        }
      } else {
        setPlayingAudio(false);
      }
    } catch (err) {
      console.error('Failed to play pronunciation:', err);
      setPlayingAudio(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current = null;
      }
    };
  }, []);

  // Restart session
  const handleRestart = () => {
    setCurrentIndex(0);
    setIsSessionComplete(false);
    setWordProgressMap({});
    clearAllStates();
    loadVocabulary();
  };

  // Loading state
  if (loading) {
    return (
      <div className="vocab-practice-page">
        <div className="vocab-practice-loading">
          <div className="loading-spinner"></div>
          <p>{t('vocabularyPractice.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="vocab-practice-page">
        <div className="vocab-practice-error">
          <p>{error}</p>
          <button onClick={loadVocabulary} className="btn-retry">{t('vocabularyPractice.retry')}</button>
        </div>
      </div>
    );
  }

  // Empty state
  if (vocabulary.length === 0) {
    return (
      <div className="vocab-practice-page">
        <div className="vocab-practice-empty">
          <p>{t('vocabularyPractice.empty')}</p>
          <button onClick={() => navigate(-1)} className="btn-back">{t('vocabularyPractice.back')}</button>
        </div>
      </div>
    );
  }

  // Session complete
  if (isSessionComplete) {
    return (
      <div className="vocab-practice-page">
        <div className="session-complete">
          <div className="complete-icon">
            <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <h2>{t('vocabularyPractice.complete.title')}</h2>
          <p className="complete-stats">{t('vocabularyPractice.complete.stats', { count: vocabulary.length })}</p>

          <PhonicsSessionSummary errors={getSessionErrors()} />

          <div className="complete-actions">
            <button onClick={handleRestart} className="btn-primary">
              {t('vocabularyPractice.complete.restart')}
            </button>
            <button onClick={() => navigate(-1)} className="btn-outline">
              {t('vocabularyPractice.complete.backToCourse')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentItem = vocabulary[currentIndex];

  return (
    <div className="vocab-practice-page">
      {/* Header */}
      <div className="learning-mode-header">
        <h2 className="learning-mode-header__title">{t('vocabulary.title')}</h2>
        {currentStage === 'dictation' && <InputModeToggle isLetterPicker={isLetterPicker} onToggle={() => { toggleInputMode(); setLetterPickerResetKey(k => k + 1); }} />}
        <div className="learning-mode-header__nav">
          <button
            className="sentence-nav-btn"
            onClick={() => { if (currentIndex > 0) { setCurrentIndex(currentIndex - 1); clearAllStates(); window.scrollTo({ top: 0, behavior: 'smooth' }); } }}
            disabled={currentIndex === 0}
            aria-label={t('navigation.previousWord')}
          >
            ◀
          </button>
          <span className="sentence-nav-counter">{currentIndex + 1} / {vocabulary.length}</span>
          <button
            className="sentence-nav-btn"
            onClick={handleSkipWord}
            disabled={!canSkipWord}
            aria-label={t('navigation.nextWord')}
          >
            ▶
          </button>
        </div>
      </div>

      {/* Mastery Delta Animation */}
      {masteryDelta !== null && (
        <div className={`mastery-delta ${masteryDelta > 0 ? 'positive' : 'negative'}`}>
          {masteryDelta > 0 ? `+${masteryDelta}` : masteryDelta}
        </div>
      )}

      {/* Main Card */}
      <div className="vocab-practice-container">
        <div className={`vocab-practice-card ${currentStage === 'dictation' && letterInputs.length > 7 && letterInputs.length <= 14 ? 'wide-card' : ''}`}>
          {/* Speaking Stage */}
          {currentStage === 'speaking' && (
            <div className="stage-content speaking-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                  <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                </svg>
                <span>{t('vocabularyPractice.speaking.title')}</span>
              </div>

              <div className="word-row">
                <h2 className="word">{currentItem.word}</h2>
                <button
                  className={`audio-btn-inline ${playingAudio ? 'playing' : ''}`}
                  onClick={() => playPronunciation(currentItem.word)}
                  title={t('vocabularyPractice.playPronunciation')}
                >
                  {playingAudio ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                  )}
                </button>
              </div>

              {pronunciationResult && !pronunciationResult.isCorrect && currentItem.pronunciation && (
                <p className="pronunciation">/{convertPhonetic(currentItem.pronunciation, phoneticSystem)}/</p>
              )}

              <div className="pronunciation-section">
                {(recordingError || pronunciationError) && (
                  <div className="pronunciation-error">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <span>{recordingError || pronunciationError}</span>
                  </div>
                )}

                {isCheckingPronunciation ? (
                  <div className="checking-status">
                    <div className="checking-spinner"></div>
                    <span>{t('vocabularyPractice.pronunciation.checking')}</span>
                  </div>
                ) : pronunciationResult ? (
                  <div className={`pronunciation-result ${pronunciationResult.isCorrect ? 'correct' : 'incorrect'}`}>
                    {pronunciationResult.isCorrect ? (
                      <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        <span className="feedback-text">{pronunciationResult.feedback || t('vocabularyPractice.pronunciation.correct')}</span>
                        <button className="next-btn" onClick={handleSpeakingComplete}>
                          {t('vocabularyPractice.speaking.goToDictation')}
                        </button>
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        <span className="feedback-text">{pronunciationResult.feedback || t('vocabularyPractice.pronunciation.soundsLike', { text: pronunciationResult.transcribedText })}</span>
                        <button className="retry-btn" onClick={clearPronunciationState}>
                          {t('vocabularyPractice.tryAgain')}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <button
                    className={`record-toggle-btn ${isRecording ? 'recording' : ''}`}
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else {
                        setPronunciationResult(null);
                        setPronunciationError(null);
                        startRecording();
                      }
                    }}
                  >
                    {isRecording ? (
                      <>
                        <span className="recording-indicator"></span>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                          <path d="M6 6h12v12H6z"/>
                        </svg>
                        <span>{t('vocabularyPractice.recording.stop')}</span>
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5zm6 6c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                        </svg>
                        <span>{t('vocabularyPractice.recording.start')}</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              <p className="stage-hint">{t('vocabularyPractice.speaking.hint')}</p>
            </div>
          )}

          {/* Dictation Stage */}
          {currentStage === 'dictation' && (
            <div className="stage-content dictation-stage">
              <div className="stage-label">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                </svg>
                <span>{t('vocabularyPractice.dictation.title')}</span>
              </div>

              <button
                className={`dictation-audio-btn ${playingAudio ? 'playing' : ''}`}
                onClick={() => playPronunciation(currentItem.word)}
                title={t('vocabularyPractice.playPronunciation')}
              >
                {playingAudio ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                )}
              </button>
              <p className="dictation-instruction">{t('vocabularyPractice.dictation.instruction')}</p>

              {isLetterPicker && dictationResult !== 'correct' ? (
                <LetterPicker
                  expectedText={currentItem.word}
                  onComplete={handleVocabLetterPickerComplete}
                  onErrorData={addPhonicsErrors}
                  onLetterError={handleLetterError}
                  resetKey={letterPickerResetKey}
                />
              ) : !isLetterPicker ? (
                <>
                  {/* Letter Boxes - Always shown */}
                  <div className={`letter-boxes-container ${
                    letterInputs.length > 14 ? 'extra-small-boxes' :
                    letterInputs.length > 10 ? 'small-boxes' :
                    letterInputs.length > 7 ? 'wide-layout' : ''
                  }`}>
                    {(() => {
                      // Find the first empty input index for auto-hint
                      const normalizedWord = normalizeWhitespace(currentItem.word);
                      const firstEmptyIndex = letterInputs.findIndex((l, i) => !lockedLetters[i] && l === '' && normalizedWord[i] !== ' ');

                      return letterInputs.map((letter, index) => {
                        // Space separator - display as a gap
                        if (letter === ' ' && lockedLetters[index]) {
                          return (
                            <span key={index} className="letter-box-space" aria-hidden="true">
                              &nbsp;
                            </span>
                          );
                        }
                        // Locked letter (correctly typed)
                        if (lockedLetters[index]) {
                          return (
                            <span key={index} className="letter-box locked">
                              {letter}
                            </span>
                          );
                        }

                        // Determine if this input should show auto-hint
                        // Hint shows on first empty input; disappears when user types (letter !== '')
                        const isFirstEmpty = index === firstEmptyIndex;
                        const shouldShowHint = showAutoHint && isFirstEmpty && !letter;
                        const hintLetter = shouldShowHint ? normalizedWord[index] : '';

                        // Input box for user to type
                        return (
                          <input
                            key={index}
                            ref={(el) => { letterInputRefs.current[index] = el; }}
                            type="text"
                            className={`letter-box input ${shouldShowHint ? 'with-hint' : ''}`}
                            value={letter}
                            placeholder={hintLetter}
                            onChange={(e) => handleLetterInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleLetterKeyDown(index, e)}
                            maxLength={2}
                            autoFocus={index === lockedLetters.findIndex(l => !l)}
                          />
                        );
                      });
                    })()}
                  </div>
                  <div className="letter-box-actions">
                    {dictationResult === 'correct' ? (
                      <button className="dictation-submit-btn success" onClick={handleDictationComplete}>
                        {t('vocabularyPractice.dictation.next')}
                      </button>
                    ) : (
                      <button
                        className="dictation-submit-btn"
                        onClick={handleLetterBoxSubmit}
                      >
                        {t('vocabularyPractice.dictation.submit')}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="letter-box-actions">
                  <button className="dictation-submit-btn success" onClick={handleDictationComplete}>
                    {t('vocabularyPractice.dictation.next')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <XpToastContainer toasts={xpToasts} />
    </div>
  );
};

export default VocabularyPage;
